import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["filterPanel", "cardStack"];

  // Drag state
  startX = 0
  startY = 0
  deltaX = 0
  isDragging = false
  isSwiping = false

  connect() {
    this.currentCardIndex = 0;
    this.currentPage = 1;
    this.isLoading = false;
    this.initializeFilters();
    this.restoreFilters();
    this.attachDragToTopCard();
    // If the deck arrives empty (e.g. everyone has already been swiped), show
    // the empty state immediately instead of leaving a blank card area with
    // floating action buttons over it.
    this.showEmptyStateIfNoCards();
    this._boundClickOutside = this._onClickOutsideFilter.bind(this);
    document.addEventListener("click", this._boundClickOutside, true);
  }

  disconnect() {
    document.removeEventListener("click", this._boundClickOutside, true);
  }

  attachDragToTopCard() {
    if (!this.hasCardStackTarget) return;
    const card = this.cardStackTarget.querySelector(".profile-card");
    if (card) this.attachDragListeners(card);
  }

  attachDragListeners(card) {
    card.addEventListener("pointerdown", this.onPointerDown.bind(this));
    card.addEventListener("pointermove", this.onPointerMove.bind(this));
    card.addEventListener("pointerup",   this.onPointerUp.bind(this));
    card.addEventListener("touchstart",  this.onTouchStart.bind(this), { passive: true });
    card.addEventListener("touchmove",   this.onTouchMove.bind(this),  { passive: false });
    card.addEventListener("touchend",    this.onTouchEnd.bind(this));
  }

  onPointerDown(e) {
    // Touch input is owned by the touch* handlers below. Ignoring it here stops
    // the same gesture from being processed twice (which clobbered drag state).
    if (e.pointerType === "touch") return;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.isDragging = true;
    this._dragged = false;
    // Disable the CSS transition while dragging so the card tracks the pointer
    // 1:1 instead of rubber-banding behind it.
    e.currentTarget.style.transition = "none";
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  onPointerMove(e) {
    if (e.pointerType === "touch") return;
    if (!this.isDragging) return;
    this.deltaX = e.clientX - this.startX;
    if (Math.abs(this.deltaX) > 6 || Math.abs(e.clientY - this.startY) > 6) this._dragged = true;
    this._applyDrag(e.currentTarget, this.deltaX);
  }

  onPointerUp(e) {
    if (e.pointerType === "touch") return;
    if (!this.isDragging) return;
    this._settleDrag(e.currentTarget);
  }

  onTouchStart(e) {
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.isDragging = true;
    this._dragged = false;
    e.currentTarget.style.transition = "none";
  }

  onTouchMove(e) {
    if (!this.isDragging) return;
    const dx = e.touches[0].clientX - this.startX;
    const dy = e.touches[0].clientY - this.startY;
    // Stay hands-off until the gesture is clearly a horizontal drag. Before
    // that we neither preventDefault (so vertical scrolling still works) nor
    // move the card (so a tap doesn't shift it and flash the detail-less card
    // behind). Not calling preventDefault also lets the tap's synthetic click
    // through, so tapping a card reliably opens the profile.
    if (!this._dragged) {
      if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
      this._dragged = true;
    }
    e.preventDefault();
    this.deltaX = dx;
    this._applyDrag(e.currentTarget, this.deltaX);
  }

  onTouchEnd(e) {
    if (!this.isDragging) return;
    this._settleDrag(e.currentTarget);
  }

  _applyDrag(card, deltaX) {
    card.style.transform = `translateX(${deltaX}px) rotate(${deltaX * 0.05}deg)`;
    this._showDragOverlay(card, deltaX);
  }

  _showDragOverlay(card, deltaX) {
    let likeOverlay = card.querySelector(".drag-overlay-like");
    let passOverlay = card.querySelector(".drag-overlay-pass");

    if (!likeOverlay) {
      likeOverlay = document.createElement("div");
      likeOverlay.className = "drag-overlay-like";
      likeOverlay.style.cssText = "position:absolute;top:1rem;left:1rem;background:rgba(34,197,94,0.9);color:white;font-size:2rem;font-weight:bold;padding:0.5rem 1rem;border-radius:0.5rem;border:3px solid #16a34a;transform:rotate(-15deg);display:none;z-index:30;pointer-events:none;";
      likeOverlay.textContent = "✓ LIKE";
      card.style.position = "relative";
      card.appendChild(likeOverlay);
    }
    if (!passOverlay) {
      passOverlay = document.createElement("div");
      passOverlay.className = "drag-overlay-pass";
      passOverlay.style.cssText = "position:absolute;top:1rem;right:1rem;background:rgba(239,68,68,0.9);color:white;font-size:2rem;font-weight:bold;padding:0.5rem 1rem;border-radius:0.5rem;border:3px solid #dc2626;transform:rotate(15deg);display:none;z-index:30;pointer-events:none;";
      passOverlay.textContent = "✗ PASS";
      card.style.position = "relative";
      card.appendChild(passOverlay);
    }

    if (deltaX > 20) {
      likeOverlay.style.display = "block";
      passOverlay.style.display = "none";
    } else if (deltaX < -20) {
      passOverlay.style.display = "block";
      likeOverlay.style.display = "none";
    } else {
      likeOverlay.style.display = "none";
      passOverlay.style.display = "none";
    }
  }

  _hideDragOverlays(card) {
    const like = card.querySelector(".drag-overlay-like");
    const pass = card.querySelector(".drag-overlay-pass");
    if (like) like.style.display = "none";
    if (pass) pass.style.display = "none";
  }

  _settleDrag(card) {
    this.isDragging = false;
    if (Math.abs(this.deltaX) > 80) {
      this.swipeCard(this.deltaX > 0 ? "like" : "pass");
    } else {
      // Snap back smoothly, then hand the transition back to the stylesheet.
      card.style.transition = "transform 0.3s ease";
      card.style.transform  = "translateX(0) rotate(0deg)";
      setTimeout(() => { card.style.transition = ""; }, 300);
      this._hideDragOverlays(card);
    }
    this.deltaX = 0;
  }

  initializeFilters() {
    const minAge = document.getElementById('minAge');
    const maxAge = document.getElementById('maxAge');

    if (minAge && maxAge) {
      const onMinInput = () => {
        // Clamp min so it never crosses max - 1
        const max = parseInt(maxAge.value, 10);
        if (parseInt(minAge.value, 10) >= max) minAge.value = String(Math.max(parseInt(minAge.min, 10), max - 1));
        this._syncAgeUI();
      };
      const onMaxInput = () => {
        const min = parseInt(minAge.value, 10);
        if (parseInt(maxAge.value, 10) <= min) maxAge.value = String(Math.min(parseInt(maxAge.max, 10), min + 1));
        this._syncAgeUI();
      };
      minAge.addEventListener('input', onMinInput);
      maxAge.addEventListener('input', onMaxInput);
      this._syncAgeUI();
    }

    const minHeight = document.getElementById('minHeight');
    const maxHeight = document.getElementById('maxHeight');
    if (minHeight && maxHeight) {
      const onMinInput = () => {
        const max = parseInt(maxHeight.value, 10);
        if (parseInt(minHeight.value, 10) >= max) minHeight.value = String(Math.max(parseInt(minHeight.min, 10), max - 1));
        this._syncHeightUI();
      };
      const onMaxInput = () => {
        const min = parseInt(minHeight.value, 10);
        if (parseInt(maxHeight.value, 10) <= min) maxHeight.value = String(Math.min(parseInt(maxHeight.max, 10), min + 1));
        this._syncHeightUI();
      };
      minHeight.addEventListener('input', onMinInput);
      maxHeight.addEventListener('input', onMaxInput);
      this._syncHeightUI();
    }

    const distance = document.getElementById('distance');
    if (distance) {
      distance.addEventListener('input', () => this._syncDistanceUI());
      this._syncDistanceUI();
    }

    // Gender pills — segmented control behaviour
    this.element.querySelectorAll('.gender-pill').forEach((pill) => {
      pill.addEventListener('click', (e) => {
        // Native label click toggles the radio automatically; this just updates visual state
        this.element.querySelectorAll('.gender-pill').forEach((p) => p.classList.remove('gender-pill--selected'));
        pill.classList.add('gender-pill--selected');
      });
    });
  }

  _syncAgeUI() {
    const minAge = document.getElementById('minAge');
    const maxAge = document.getElementById('maxAge');
    const minLabel = document.getElementById('minAgeValue');
    const maxLabel = document.getElementById('maxAgeValue');
    const fill = this.element.querySelector('[data-discover-target="ageRangeFill"]');
    if (!minAge || !maxAge) return;

    const lo = parseInt(minAge.min, 10);
    const hi = parseInt(minAge.max, 10);
    const minV = parseInt(minAge.value, 10);
    const maxV = parseInt(maxAge.value, 10);
    const minPct = ((minV - lo) / (hi - lo)) * 100;
    const maxPct = ((maxV - lo) / (hi - lo)) * 100;

    if (minLabel) minLabel.textContent = minV;
    if (maxLabel) maxLabel.textContent = maxV;
    if (fill) {
      fill.style.left = `calc(${minPct}% + ${12 - 0.24 * minPct}px)`;
      fill.style.right = `calc(${100 - maxPct}% + ${0.24 * maxPct - 12 + 12}px)`;
    }
  }

  _syncHeightUI() {
    const minHeight = document.getElementById('minHeight');
    const maxHeight = document.getElementById('maxHeight');
    const minLabel = document.getElementById('minHeightValue');
    const maxLabel = document.getElementById('maxHeightValue');
    const fill = this.element.querySelector('[data-discover-target="heightRangeFill"]');
    if (!minHeight || !maxHeight) return;

    const lo = parseInt(minHeight.min, 10);
    const hi = parseInt(minHeight.max, 10);
    const minV = parseInt(minHeight.value, 10);
    const maxV = parseInt(maxHeight.value, 10);
    const minPct = ((minV - lo) / (hi - lo)) * 100;
    const maxPct = ((maxV - lo) / (hi - lo)) * 100;

    if (minLabel) minLabel.textContent = minV;
    if (maxLabel) maxLabel.textContent = maxV;
    if (fill) {
      fill.style.left = `calc(${minPct}% + ${12 - 0.24 * minPct}px)`;
      fill.style.right = `calc(${100 - maxPct}% + ${0.24 * maxPct - 12 + 12}px)`;
    }
  }

  _syncDistanceUI() {
    const distance = document.getElementById('distance');
    const label = document.getElementById('distanceValue');
    const fill = this.element.querySelector('[data-discover-target="distanceFill"]');
    if (!distance) return;
    const lo = parseInt(distance.min, 10);
    const hi = parseInt(distance.max, 10);
    const v = parseInt(distance.value, 10);
    const pct = ((v - lo) / (hi - lo)) * 100;
    if (label) label.textContent = v;
    if (fill) {
      fill.style.left = '12px';
      fill.style.right = `calc(${100 - pct}% + ${0.24 * pct - 12 + 12}px)`;
    }
  }

  restoreFilters() {
    const stored = JSON.parse(localStorage.getItem("discover_filters") || "{}");
    const minAge   = document.getElementById("minAge");
    const maxAge   = document.getElementById("maxAge");
    const distance = document.getElementById("distance");

    const clampInt = (v, lo, hi) => {
      const n = parseInt(v, 10);
      if (Number.isNaN(n)) return null;
      return Math.max(lo, Math.min(hi, n));
    };

    if (minAge && maxAge && stored.minAge && stored.maxAge) {
      const lo = parseInt(minAge.min, 10);
      const hi = parseInt(minAge.max, 10);
      let mn = clampInt(stored.minAge, lo, hi - 1);
      let mx = clampInt(stored.maxAge, lo + 1, hi);
      if (mn != null && mx != null && mn >= mx) mn = mx - 1;
      if (mn != null) minAge.value = String(mn);
      if (mx != null) maxAge.value = String(mx);
      this._syncAgeUI();
    } else {
      this._syncAgeUI();
    }

    if (distance && stored.distance) {
      const d = clampInt(stored.distance, parseInt(distance.min, 10), parseInt(distance.max, 10));
      if (d != null) distance.value = String(d);
      this._syncDistanceUI();
    } else {
      this._syncDistanceUI();
    }

    if (stored.gender) {
      const sel = this.element.querySelector(`.gender-pill input[type="radio"][value="${stored.gender}"]`);
      if (sel) {
        sel.checked = true;
        this.element.querySelectorAll('.gender-pill').forEach((p) => p.classList.remove('gender-pill--selected'));
        sel.closest('.gender-pill')?.classList.add('gender-pill--selected');
      }
    }

    const ethnicity = document.getElementById('ethnicityFilter');
    if (ethnicity && stored.ethnicity) ethnicity.value = stored.ethnicity;

    const minHeight = document.getElementById('minHeight');
    const maxHeight = document.getElementById('maxHeight');
    if (minHeight && maxHeight && stored.minHeight && stored.maxHeight) {
      const lo = parseInt(minHeight.min, 10);
      const hi = parseInt(minHeight.max, 10);
      let mn = clampInt(stored.minHeight, lo, hi - 1);
      let mx = clampInt(stored.maxHeight, lo + 1, hi);
      if (mn != null && mx != null && mn >= mx) mn = mx - 1;
      if (mn != null) minHeight.value = String(mn);
      if (mx != null) maxHeight.value = String(mx);
      this._syncHeightUI();
    } else {
      this._syncHeightUI();
    }
  }

  toggleFilters() {
    if (!this.hasFilterPanelTarget) return;
    this.filterPanelTarget.classList.toggle('active');
    const backdrop = this.element.querySelector('.filter-backdrop');
    if (backdrop) backdrop.classList.toggle('active');
  }

  _onClickOutsideFilter(event) {
    if (!this.hasFilterPanelTarget) return;
    if (!this.filterPanelTarget.classList.contains("active")) return;
    if (this.filterPanelTarget.contains(event.target)) return;
    const filterBtn = this.element.querySelector(".filter-btn");
    if (filterBtn && filterBtn.contains(event.target)) return;
    this.filterPanelTarget.classList.remove("active");
  }

  resetFilters(event) {
    if (event) event.preventDefault();
    localStorage.removeItem("discover_filters");
    window.location.href = "/dashboard/discoveries";
  }

  applyFilters(event) {
    event.preventDefault();

    const minAge = document.getElementById('minAge')?.value;
    const maxAge = document.getElementById('maxAge')?.value;
    const distance = document.getElementById('distance')?.value;
    const selectedGender = this.element.querySelector('.gender-pill input[type="radio"]:checked');
    const gender = selectedGender ? selectedGender.value : '';
    const ethnicity = document.getElementById('ethnicityFilter')?.value || '';
    const minHeight = document.getElementById('minHeight')?.value;
    const maxHeight = document.getElementById('maxHeight')?.value;

    localStorage.setItem("discover_filters", JSON.stringify({ minAge, maxAge, distance, gender, ethnicity, minHeight, maxHeight }));

    const params = new URLSearchParams();
    if (minAge) params.append('min_age', minAge);
    if (maxAge) params.append('max_age', maxAge);
    if (distance) params.append('distance', distance);
    if (gender) params.append('gender', gender);
    if (ethnicity) params.append('ethnicity', ethnicity);
    if (minHeight) params.append('min_height', minHeight);
    if (maxHeight) params.append('max_height', maxHeight);

    this.toggleFilters();
    window.location.href = `/dashboard/discoveries?${params.toString()}`;
  }

  viewProfile(event) {
    if (event.target.closest("button")) return;
    // A swipe/drag (even an aborted one that snaps back) ends in a synthetic
    // click. Without this guard that click navigates to the profile, so the
    // card appears to "disappear without any action". Only treat a genuine tap
    // (no movement) as a profile open.
    if (this._dragged) {
      this._dragged = false;
      return;
    }
    const url = event.currentTarget.dataset.profileUrl;
    if (url) Turbo.visit(url);
  }

  like() {
    this.swipeCard('like');
  }

  pass() {
    this.swipeCard('pass');
  }

  async swipeCard(action) {
    if (this.isSwiping) return;
    if (!this.hasCardStackTarget) return;
    const currentCard = this.cardStackTarget.querySelector('.profile-card');
    if (!currentCard) return;

    const userId = currentCard.dataset.userId;
    if (!userId) return;

    this.isSwiping = true;

    // Disable action buttons to prevent double-swipe (bar lives outside the card now)
    const actionButtons = this.element.querySelectorAll(".card-actions-bar .action-btn");
    actionButtons.forEach(b => { b.disabled = true; });

    const direction = action === 'like' ? 1 : -1;
    // Re-enable an explicit transition before flying out so a card that was just
    // being dragged (transition: none) animates off-screen instead of teleporting.
    currentCard.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
    currentCard.style.transform = `translateX(${direction * 100}%) rotate(${direction * 30}deg)`;
    currentCard.style.opacity = '0';

    this.showSwipeFeedback(action);

    try {
      const response = await fetch('/dashboard/swipes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          target_user_id: userId,
          swipe_type: action
        })
      });

      const data = await response.json();

      if (data.matched) {
        const matchData = {
          username: data.user.username,
          photo_url: data.user.photo_url,
          match_id: data.match_id
        };
        this.showMatchModal(matchData);
      }
    } catch (error) {
      console.error('Error swiping:', error);
    }

    setTimeout(() => {
      currentCard.remove();
      this.isSwiping = false;
      actionButtons.forEach(b => { b.disabled = false; });
      this.attachDragToTopCard();
      this.currentCardIndex++;
      if (this.cardStackTarget.children.length < 2) {
        this.loadMoreCards();
      }
    }, 300);

    this.dispatch('swipe', { detail: { action, userId } });
  }

  showSwipeFeedback(action) {
    const feedback = document.createElement('div');
    feedback.className = 'swipe-feedback';
    feedback.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 4rem;
      z-index: 9999;
      pointer-events: none;
      animation: swipeFeedback 0.6s ease-out forwards;
    `;

    feedback.textContent = action === 'like' ? '💖' : '❌';
    document.body.appendChild(feedback);

    if (!document.getElementById('swipe-feedback-styles')) {
      const style = document.createElement('style');
      style.id = 'swipe-feedback-styles';
      style.textContent = `
        @keyframes swipeFeedback {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }

    setTimeout(() => feedback.remove(), 600);
  }

  async loadMoreCards() {
    if (this.isLoading) return;

    const cardStack = this.hasCardStackTarget ? this.cardStackTarget : this.element.querySelector("#swipe_cards");
    if (!cardStack) return;

    const currentPage = parseInt(cardStack.dataset.page || "1", 10);
    const totalPages = parseInt(cardStack.dataset.totalPages || "1", 10);

    if (currentPage >= totalPages) {
      this.showEmptyState();
      return;
    }

    this.isLoading = true;
    try {
      const response = await fetch(`/dashboard/discoveries?page=${currentPage + 1}`, {
        headers: { "Accept": "text/vnd.turbo-stream.html", "X-Requested-With": "XMLHttpRequest" }
      });
      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        cardStack.dataset.page = String(currentPage + 1);
        cardStack.querySelectorAll(".profile-card:not([data-drag-attached])").forEach(card => {
          this.attachDragListeners(card);
          card.dataset.dragAttached = "true";
        });
      } else {
        this.showEmptyState();
      }
    } catch {
      this.showEmptyState();
    } finally {
      this.isLoading = false;
    }
  }

  showEmptyStateIfNoCards() {
    if (!this.hasCardStackTarget) return;
    if (!this.cardStackTarget.querySelector('.profile-card')) {
      this.showEmptyState();
    }
  }

  showEmptyState() {
    if (!this.hasCardStackTarget) return;
    const emptyState = this.element.querySelector('[data-discover-empty]');
    if (emptyState) emptyState.classList.remove('hidden');
    this.cardStackTarget.classList.add('hidden');
    // Hide the like/pass bar — there's nothing to act on.
    const actionsBar = this.element.querySelector('.card-actions-bar');
    if (actionsBar) actionsBar.classList.add('hidden');
  }

  showMatchModal(matchData) {
    const modal = document.createElement('div');
    modal.className = 'match-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, #ff6b9d, #e11d48);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      color: white;
      text-align: center;
      animation: matchModalIn 0.5s ease-out;
    `;

    const userName = matchData && matchData.username ? matchData.username : 'Someone';
    const photoUrl = matchData && matchData.photo_url ? matchData.photo_url : '';
    const matchId = matchData && matchData.match_id ? matchData.match_id : null;
    const conversationUrl = matchId ? `/dashboard/conversations/${matchId}` : '/dashboard/conversations';

    modal.innerHTML = `
      <div style="font-size: 6rem; margin-bottom: 2rem; animation: bounce 1s infinite;">💖</div>
      <h2 style="font-size: 3rem; margin-bottom: 1rem; font-weight: bold;">It's a Match!</h2>
      <p style="font-size: 1.25rem; margin-bottom: 1rem; opacity: 0.9;">You and ${userName} liked each other</p>
      ${photoUrl ? `<img src="${photoUrl}" alt="${userName}" style="width: 150px; height: 150px; border-radius: 50%; object-fit: cover; margin: 1rem 0; border: 4px solid white;">` : ''}
      <div style="display: flex; gap: 1rem; margin-top: 2rem;">
        <button data-action="click->discover#closeMatchModal"
                style="background: rgba(255,255,255,0.2); color: white; border: 2px solid white; padding: 1rem 2rem; border-radius: 9999px; font-weight: 600; cursor: pointer;">
          Keep Swiping
        </button>
        <a href="${conversationUrl}"
           data-turbo="false"
           style="background: white; color: #e11d48; border: none; padding: 1rem 2rem; border-radius: 9999px; font-weight: 600; cursor: pointer; text-decoration: none;">
          💬 Send Message
        </a>
      </div>
    `;

    if (!document.getElementById('match-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'match-modal-styles';
      style.textContent = `
        @keyframes matchModalIn { 0% { opacity: 0; transform: scale(0.8); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes bounce { 0%,20%,50%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-20px); } 60% { transform: translateY(-10px); } }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(modal);

    setTimeout(() => {
      if (modal.parentNode) modal.remove();
    }, 5000);
  }

  closeMatchModal(event) {
    const modal = document.querySelector('.match-modal');
    if (modal) modal.remove();
  }
}
